
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';

@Component({
  selector: 'app-chat-camera-dialog',
  templateUrl: './chat-camera-dialog.component.html',
  styleUrls: ['./chat-camera-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ChatCameraDialogComponent {

  @Input() isMobileApp: boolean = false;

  @ViewChild("video") public video: ElementRef;  
  @ViewChild("canvas") public canvas: ElementRef;

  loading: boolean = true;
  error: any;
  captureMode: 'environment' | 'user' = 'environment';

  private stream: MediaStream;

  height: number;
  width: number;

  cameraIdx: number = 0;

  @Output() capture = new EventEmitter<string>();
  @Output() close = new EventEmitter<void>();

  async ngOnInit() {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      // Permissions granted, now enumerate devices
      this.availableVideoInputs = await this.getAvailableVideoInputs();
    } catch (error) {
      console.error("Error accessing media devices.", error);
    }
  
    // Listen for changes in the list of media devices and update accordingly
    navigator.mediaDevices.addEventListener('devicechange', async () => {
      this.availableVideoInputs = await this.getAvailableVideoInputs();
    });
  }

  async ngAfterViewInit() {
    this.availableVideoInputs = (await this.getAvailableVideoInputs()).slice(0, 2); // Limit to 2 cameras
    if (this.availableVideoInputs.length > 0) {
      if (this.availableVideoInputs.length > 1) {
        this.cameraIdx = 1;
      }
      const constraint = this.getMediaConstraintsForDevice(this.availableVideoInputs[this.cameraIdx].deviceId);
      await this.initializeCamera(constraint);
    }
  }

  async onToggleCamera() {
    this.cameraIdx = this.cameraIdx === 0 ? 1 : 0;
    if (this.availableVideoInputs.length == 2) {
      const constraint = this.getMediaConstraintsForDevice(this.availableVideoInputs[this.cameraIdx].deviceId);
      await this.initializeCamera(constraint);
    }
  }

  availableVideoInputs: MediaDeviceInfo[] = [];
  async initializeCamera(videoTrackConstraints: MediaTrackConstraints) {
    this.loading = true;
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const mediaStreamConstraints = <MediaStreamConstraints>{video: videoTrackConstraints}
        this.stream = await navigator.mediaDevices.getUserMedia(mediaStreamConstraints);
        if (this.stream) {
          this.video.nativeElement.srcObject = this.stream;
          this.video.nativeElement.play();
          this.video.nativeElement.onloadedmetadata = () => {
            // set canvas dimensions
            this.height = this.video.nativeElement.videoHeight;
            this.width = this.video.nativeElement.videoWidth;
          }
          
          this.error = null;
        } else {
          this.error = "No output video device detected";
        }
      } catch (e) {
        this.error = e;
      } finally {
        this.loading = false;
      }
    }
  }
  
  onCaptureImage() {
    this.canvas.nativeElement.getContext('2d').drawImage(this.video.nativeElement, 0, 0, this.width, this.height);
    const imageUrl = this.canvas.nativeElement.toDataURL('image/png');
    this.capture.emit(imageUrl);
  }

  onClose() {
    this.close.emit();
  }

  ngOnDestroy() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
  }

  private async getAvailableVideoInputs(): Promise<MediaDeviceInfo[]> {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return Promise.resolve([]);
    }

    return new Promise((resolve, reject) => {
      navigator.mediaDevices.enumerateDevices()
        .then((devices: MediaDeviceInfo[]) => {
          resolve(devices.filter((device: MediaDeviceInfo) => device.kind === 'videoinput'));
        })
        .catch(err => {
          reject(err.message || err);
        });
    });
  }

  private getMediaConstraintsForDevice(deviceId: string, baseMediaTrackConstraints?: MediaTrackConstraints): MediaTrackConstraints {
    const result: MediaTrackConstraints = baseMediaTrackConstraints ? baseMediaTrackConstraints : {};
    if (deviceId) {
      result.deviceId = {exact: deviceId};
    }
    return result;
  }
}